// janus-engine/ability/orbs/enemy-autopick.ts — the ENGINE's enemy auto-pick (validate_and_adjust_targets.php §2 + includes/functions/janus/
// encounter/pick_random_enemy_target.php), Janus lane A10 (2026-10-02, board R-026 plan F2 — the Machinist orb strikes).
//
// An ability carrying target_all_enemies / target_random_enemy (+ target_random_enemy_qty) / target_nearest_enemy / target_lowest_vitality_enemy
// that reaches janus() with NO target (an orb pulse / surge — ft_fire_orb_ability passes targets_arr []) gets its targets from the engine:
// every living opposing-team combatant (ft_enemy_target_candidates: roster order, never the caster, not dead, vitality > 0, never a lair,
// check_opposing_team — a neutral / unteamed caster finds none), then
//   all enemies    → the whole pool, roster order;
//   random enemy   → ft_random_enemy_sample(pool, max(1, qty)): a partial Fisher-Yates through the dice seam — draw i is one die of size
//                    n − i (none when one candidate is left), the same dice PHP draws since lane A10 (it drew from array_rand / shuffle);
//   lowest vitality → the first strict minimum of v_current.
// The picked targets are exempt from the geometry gates (range, sight, line of effect, the single-target count) — the layer's contract.
// HANDED BACK: target_nearest_enemy (calculate_range_from_caster_to_target's metric is not ported — no authored orb uses it), an own-inventory
// picker on the same ability, a candidate the page does not hold as a member.
import { type JanusCtx, type Row, ecId, ecLean, handBack, int, itemField, record, scalar } from '../context.js';
import { checkOpposingTeam } from '../check-opposing-team.js';

export const ENEMY_AUTOPICK_LAYERS = ['target_all_enemies', 'target_random_enemy', 'target_nearest_enemy', 'target_lowest_vitality_enemy'] as const;

/** Does janus() §2 auto-pick this ability's targets (it carries one of the four enemy-pick layers)? */
export function hasEnemyAutopick(ctx: JanusCtx, ability: Row): boolean {
  return ENEMY_AUTOPICK_LAYERS.some((key) => ctx.read.has(ability, key));
}

/** computed_value(candidate, 'vitality', 'v_current'): the member's live value (this use's own damage included), else the compiled one. */
function vitalityOf(member: Row): number {
  const raw = ecLean(member, 'v_current') ?? record(record(member.computed)?.vitality)?.v_current;
  if (raw === null || raw === undefined || raw === '') handBack('validate_and_adjust_targets:autopick-vitality-not-held');
  return int(raw);
}

/** ft_enemy_target_candidates(): the members of the encounter blob, in roster order, that the pick may land on. */
export function enemyCandidates(ctx: JanusCtx, caster: Row): Row[] {
  const casterId = ecId(caster);
  const roster = Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr as unknown[] : [];
  const out: Row[] = [];
  for (const entry of roster) {
    const row = record(entry);
    const id = ecId(row);
    if (!row || id === '' || id === casterId) continue;
    const member = ctx.members.get(id);
    if (!member) handBack('validate_and_adjust_targets:autopick-candidate-not-member');
    if (Number(scalar(ecLean(member!, 'encounter_creature_dead')) ?? 0) === 1) continue;
    if (vitalityOf(member!) <= 0) continue;
    if (String(itemField(member!, 'item_type') ?? ecLean(member!, 'item_type') ?? '') === 'lair') continue;
    if (!checkOpposingTeam(ctx, caster, member!)) continue;
    out.push(member!);
  }
  return out;
}

/** ft_random_enemy_sample(): `take` distinct candidates in random order, one die of size n − i per draw i (none for the last one). */
function randomSample(ctx: JanusCtx, candidates: Row[], take: number): Row[] {
  const pool = [...candidates];
  const n = pool.length;
  const k = Math.max(0, Math.min(take, n));
  for (let i = 0; i < k && i < n - 1; i++) {
    const drawn = ctx.roll.d(n - i);
    const j = i + (ctx.randomPickProbe?.(n - i, drawn) ?? drawn) - 1;
    if (j !== i) [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  return pool.slice(0, k);
}

/**
 * validate_and_adjust_targets §2's enemy auto-pick for a dispatch that reached janus() with no target. Null = the ability carries no
 * enemy-pick layer (the caller's own empty-target handling applies). [] = no candidate (PHP then vetoes "requires a target" — a silent skip
 * inside a trigger frame).
 */
export function enemyAutopick(ctx: JanusCtx, ability: Row, caster: Row): Row[] | null {
  if (!hasEnemyAutopick(ctx, ability)) return null;
  // ability_uses_caster_inventory_target_picker() (an ability_works_only_against_item_type gate without a team layer): §2 skips the pick
  // for an own-inventory item gate — not modelled together → PHP
  if (ctx.read.has(ability, 'ability_works_only_against_item_type')) handBack('validate_and_adjust_targets:autopick-inventory-picker');
  if (ctx.read.has(ability, 'target_all_enemies')) return enemyCandidates(ctx, caster);
  if (ctx.read.has(ability, 'target_random_enemy')) {
    const qty = ctx.read.has(ability, 'target_random_enemy_qty') ? int(ctx.read.value(ability, 'target_random_enemy_qty')) : 0;
    return randomSample(ctx, enemyCandidates(ctx, caster), Math.max(1, qty));
  }
  if (ctx.read.has(ability, 'target_nearest_enemy')) handBack('validate_and_adjust_targets:autopick-nearest-enemy');
  // target_lowest_vitality_enemy: the first STRICT minimum (ties: first found), as ft_pick_lowest_vitality_enemy_target
  let best: Row | null = null, bestV = Number.MAX_SAFE_INTEGER;
  for (const candidate of enemyCandidates(ctx, caster)) {
    const v = vitalityOf(candidate);
    if (v < bestV) { best = candidate; bestV = v; }
  }
  return best ? [best] : [];
}
